// src/index.ts
import {
  isOrderAssignable,
  isRideRetractable as isRideRetractable2,
  RETRACTABLE_RIDE_STATUSES,
  isOfferAccepted as isOfferAccepted3,
  resolveOfferAuditFields as resolveOfferAuditFields2,
  resolveDriverAssignment as resolveDriverAssignment2,
  formatOfferActor as formatOfferActor2,
  formatOfferTime,
  formatAuditTime as formatAuditTime2
} from "@hopdrive/sdk-dispatch";
import {
  presentationForOfferStatus as presentationForOfferStatus2,
  presentationForAssignmentState as presentationForAssignmentState2
} from "@hopdrive/sdk-ui/offer-status";

// src/components/RunDetailOffer.tsx
import { css } from "@emotion/css";
import { useTheme, Avatar, Box, Button, Chip, Typography, CircularProgress } from "@mui/material";
import { BiSolidUserCheck, BiSolidUserX, BiTransfer } from "react-icons/bi";
import { presentationForOfferStatus, presentationForAssignmentState } from "@hopdrive/sdk-ui/offer-status";
import { isOfferAccepted, resolveOfferAuditFields, formatOfferActor, formatAuditTime } from "@hopdrive/sdk-dispatch";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var RunDetailOffer = ({
  offers,
  runDriver,
  pendingDriver,
  assignment,
  assignButtonStyle,
  canAssign = false,
  isAssigningOrUnassigning,
  onAssignOrUnassign,
  onUnassignWithoutDriver,
  onReassign,
  trailing
}) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const hasDriver = !!(runDriver || offers?.length || assignment?.driver || pendingDriver);
  const auditSlots = (offer) => {
    const { offeredAt, offeredBy, acceptedAt, acceptedBy } = resolveOfferAuditFields(offer);
    const formattedOfferedTime = formatAuditTime(offeredAt);
    const formattedOfferedActor = formatOfferActor(offeredBy);
    const formattedAcceptedTime = formatAuditTime(acceptedAt);
    const formattedAcceptedActor = formatOfferActor(acceptedBy);
    return /* @__PURE__ */ jsxs("div", { className: cls.audit, children: [
      /* @__PURE__ */ jsxs("div", { className: cls.auditSlot, children: [
        /* @__PURE__ */ jsx("span", { className: cls.auditLabel, children: "Offered" }),
        formattedOfferedTime ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: cls.auditTime, children: formattedOfferedTime }),
          formattedOfferedActor !== "\u2014" && /* @__PURE__ */ jsxs("span", { className: cls.auditActor, children: [
            "by ",
            formattedOfferedActor
          ] })
        ] }) : /* @__PURE__ */ jsx("span", { className: cls.auditPending, children: "\u2014" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: cls.auditSlot, children: [
        /* @__PURE__ */ jsx("span", { className: cls.auditLabel, children: "Accepted" }),
        formattedAcceptedTime ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: cls.auditTime, children: formattedAcceptedTime }),
          formattedAcceptedActor !== "\u2014" && /* @__PURE__ */ jsxs("span", { className: cls.auditActor, children: [
            "by ",
            formattedAcceptedActor
          ] })
        ] }) : isOfferAccepted(offer) ? (
          // Committed (accepted/assigned) but the accept moment wasn't stamped — an
          // 'assigned' row with no accepted_at. The driver isn't awaited; the time is
          // just unknown, so it reads as a dash beside the Accepted chip.
          /* @__PURE__ */ jsx("span", { className: cls.auditPending, children: "\u2014" })
        ) : /* @__PURE__ */ jsx("span", { className: cls.auditAwaiting, children: "Awaiting driver" })
      ] })
    ] });
  };
  const actionButton = () => {
    if (!onAssignOrUnassign) return null;
    return /* @__PURE__ */ jsx(
      Button,
      {
        disabled: !canAssign || isAssigningOrUnassigning,
        startIcon: hasDriver ? /* @__PURE__ */ jsx(BiSolidUserX, {}) : /* @__PURE__ */ jsx(BiSolidUserCheck, {}),
        variant: "outlined",
        color: hasDriver ? "error" : "primary",
        size: "small",
        onClick: onAssignOrUnassign,
        sx: { ml: hasDriver && onReassign ? 1 : "auto", flexShrink: 0, alignSelf: "center" },
        style: !hasDriver ? assignButtonStyle : void 0,
        children: hasDriver ? "Unassign" : "Assign Driver"
      }
    );
  };
  const reassignButton = () => {
    if (!hasDriver || !onReassign) return null;
    return /* @__PURE__ */ jsx(
      Button,
      {
        disabled: !canAssign || isAssigningOrUnassigning,
        startIcon: /* @__PURE__ */ jsx(BiTransfer, {}),
        variant: "outlined",
        color: "primary",
        size: "small",
        onClick: onReassign,
        sx: { ml: "auto", flexShrink: 0, alignSelf: "center" },
        children: "Reassign"
      }
    );
  };
  const stuckUnassignButton = () => {
    if (hasDriver || !onUnassignWithoutDriver) return null;
    return /* @__PURE__ */ jsx(
      Button,
      {
        disabled: !canAssign || isAssigningOrUnassigning,
        startIcon: /* @__PURE__ */ jsx(BiSolidUserX, {}),
        variant: "outlined",
        color: "error",
        size: "small",
        onClick: onUnassignWithoutDriver,
        sx: { ml: 1, flexShrink: 0, alignSelf: "center" },
        children: "Unassign"
      }
    );
  };
  const initials = (name) => name?.split(" ").map((n) => n[0]).join("").slice(0, 2);
  const nameBlock = (name, phone) => /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", flexDirection: "column", minWidth: 0 }, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { fontWeight: 500 }, children: name }),
    phone ? /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", children: phone }) : null
  ] });
  const statusChip = (status) => status ? /* @__PURE__ */ jsx(
    Chip,
    {
      size: "small",
      variant: "outlined",
      label: status === "online" ? "Online" : "Offline",
      color: status === "online" ? "success" : "default",
      sx: { fontSize: 10, height: 20 }
    }
  ) : null;
  const renderBody = () => {
    if (isAssigningOrUnassigning) {
      return /* @__PURE__ */ jsx("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
        /* @__PURE__ */ jsx(CircularProgress, { size: 20, sx: { color: theme?.palette?.text?.disabled } }),
        /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { fontWeight: 500, color: theme?.palette?.text?.disabled }, children: "Updating..." })
      ] }) });
    }
    if (offers?.length) {
      return /* @__PURE__ */ jsx("div", { className: cls.offerList, children: offers.map((offer) => {
        const { Icon: StatusIcon, tone, label } = presentationForOfferStatus(offer.status);
        return /* @__PURE__ */ jsxs("div", { className: cls.offerRow, children: [
          /* @__PURE__ */ jsxs("div", { className: cls.driverInfo, children: [
            /* @__PURE__ */ jsx(
              Avatar,
              {
                alt: offer.driver?.user?.display_name || "",
                src: offer.driver?.user?.avatar_url || "",
                sx: { width: 40, height: 40, bgcolor: theme?.palette?.grey?.[400] },
                children: initials(offer.driver?.user?.display_name)
              }
            ),
            nameBlock(offer.driver?.user?.display_name, offer.driver?.user?.phone),
            statusChip(offer.driver?.status),
            /* @__PURE__ */ jsx(
              Chip,
              {
                icon: /* @__PURE__ */ jsx(StatusIcon, { size: 16 }),
                size: "small",
                color: tone,
                label,
                sx: { fontWeight: 500, ml: 1 }
              }
            )
          ] }),
          auditSlots(offer),
          reassignButton(),
          actionButton()
        ] }, `offer-${offer.id}`);
      }) });
    }
    if (runDriver) {
      return /* @__PURE__ */ jsx("div", { className: cls.offerList, children: /* @__PURE__ */ jsxs("div", { className: cls.offerRow, children: [
        /* @__PURE__ */ jsxs("div", { className: cls.driverInfo, children: [
          /* @__PURE__ */ jsx(
            Avatar,
            {
              alt: runDriver.user?.display_name || "",
              src: runDriver.user?.avatar_url || "",
              sx: { width: 40, height: 40, bgcolor: theme?.palette?.grey?.[400] },
              children: initials(runDriver.user?.display_name)
            }
          ),
          nameBlock(runDriver.user?.display_name || "Assigned driver", runDriver.user?.phone),
          statusChip(runDriver.status),
          /* @__PURE__ */ jsx(
            Chip,
            {
              size: "small",
              color: "success",
              label: "Driver Assigned",
              sx: { fontWeight: 500, ml: 1 }
            }
          )
        ] }),
        reassignButton(),
        actionButton()
      ] }) });
    }
    if (assignment?.driver) {
      const { Icon: StatusIcon, tone } = presentationForAssignmentState(assignment.state);
      return /* @__PURE__ */ jsx("div", { className: cls.offerList, children: /* @__PURE__ */ jsxs("div", { className: cls.offerRow, children: [
        /* @__PURE__ */ jsxs("div", { className: cls.driverInfo, children: [
          /* @__PURE__ */ jsx(
            Avatar,
            {
              alt: assignment.driver.display_name || "",
              src: assignment.driver.avatar_url || "",
              sx: { width: 40, height: 40, bgcolor: theme?.palette?.grey?.[400] },
              children: initials(assignment.driver.display_name)
            }
          ),
          /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { fontWeight: 500 }, children: assignment.driver.display_name || "Assigned driver" }),
          /* @__PURE__ */ jsx(
            Chip,
            {
              icon: /* @__PURE__ */ jsx(StatusIcon, { size: 16 }),
              size: "small",
              color: tone,
              label: "Driver Assigned",
              sx: { fontWeight: 500, ml: 1 }
            }
          )
        ] }),
        reassignButton(),
        actionButton()
      ] }) });
    }
    if (pendingDriver) {
      return /* @__PURE__ */ jsx("div", { className: cls.offerList, children: /* @__PURE__ */ jsxs("div", { className: cls.offerRow, children: [
        /* @__PURE__ */ jsxs("div", { className: cls.driverInfo, children: [
          /* @__PURE__ */ jsx(
            Avatar,
            {
              alt: pendingDriver.user?.display_name || "",
              src: pendingDriver.user?.avatar_url || "",
              sx: { width: 40, height: 40, bgcolor: theme?.palette?.grey?.[400] },
              children: initials(pendingDriver.user?.display_name)
            }
          ),
          nameBlock(pendingDriver.user?.display_name || "Targeted driver", pendingDriver.user?.phone),
          statusChip(pendingDriver.status),
          /* @__PURE__ */ jsx(
            Chip,
            {
              size: "small",
              color: "info",
              label: "Dispatching\u2026",
              sx: { fontWeight: 500, ml: 1 }
            }
          )
        ] }),
        reassignButton(),
        actionButton()
      ] }) });
    }
    return /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [
      /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { fontWeight: 500, color: theme?.palette?.text?.secondary }, children: "No driver assigned" }),
      /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", ml: "auto" }, children: [
        reassignButton(),
        actionButton(),
        stuckUnassignButton()
      ] })
    ] });
  };
  return /* @__PURE__ */ jsx("div", { className: cls.offer, children: /* @__PURE__ */ jsxs("div", { className: cls.panelRow, children: [
    /* @__PURE__ */ jsx("div", { className: cls.panelMain, children: renderBody() }),
    trailing && !isAssigningOrUnassigning ? /* @__PURE__ */ jsx(Box, { sx: { flexShrink: 0 }, children: trailing }) : null
  ] }) });
};
var useStyles = (theme) => ({
  offer: css`
    padding: 16px 20px;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    margin-top: 12px;
    margin-bottom: 24px;
    background-color: ${theme?.palette?.background?.paper};
  `,
  panelRow: css`
    display: flex;
    align-items: center;
    gap: 12px;
  `,
  panelMain: css`
    flex: 1;
    min-width: 0;
  `,
  offerList: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  offerRow: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  `,
  driverInfo: css`
    display: flex;
    align-items: center;
    gap: 12px;
  `,
  audit: css`
    display: flex;
    gap: 32px;
  `,
  auditSlot: css`
    display: flex;
    flex-direction: column;
    min-width: 140px;
  `,
  auditLabel: css`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${theme?.palette?.text?.disabled};
    margin-bottom: 2px;
  `,
  auditTime: css`
    font-size: 14px;
    font-weight: 500;
    color: ${theme?.palette?.text?.primary};
  `,
  auditActor: css`
    font-size: 13px;
    color: ${theme?.palette?.text?.secondary};
  `,
  auditAwaiting: css`
    font-size: 14px;
    font-style: italic;
    color: ${theme?.palette?.text?.disabled};
  `,
  auditPending: css`
    font-size: 14px;
    color: ${theme?.palette?.text?.disabled};
  `
});
var RunDetailOffer_default = RunDetailOffer;

// src/components/MoveDetailsOffer.tsx
import { Button as Button2 } from "@mui/material";
import { BiCar } from "react-icons/bi";

// src/moveDetailsAssignment.ts
import { isRideRetractable, resolveDriverAssignment } from "@hopdrive/sdk-dispatch";
var hasOffers = (link) => (link?.scenario?.bundletoscenarios?.[0]?.bundle?.offers?.length ?? 0) > 0;
var findCurrentScenario = (move) => {
  const links = move?.scenariotomoves ?? [];
  return (links.find(hasOffers) ?? links[0])?.scenario ?? null;
};
var findOffersInScenarios = (scenariotomoves) => {
  const offers = scenariotomoves?.find((link) => (link?.scenario?.bundletoscenarios?.[0]?.bundle?.offers?.length ?? 0) > 0)?.scenario?.bundletoscenarios?.[0]?.bundle?.offers;
  return offers ?? void 0;
};
var toLegInputs = (scenario) => (scenario?.moves ?? []).filter((member) => !!member?.move && member.move.active !== 0).map((member) => ({
  cardinality: member.cardinality,
  move: {
    move_type: member.move.move_type,
    status: member.move.status ?? null,
    lite: member.move.lite ?? null,
    driver_id: member.move.driver_id ?? null,
    driver_name: member.move.driver_name ?? null
  }
}));
var toOfferInputs = (scenario) => (scenario?.bundletoscenarios?.[0]?.bundle?.offers ?? []).filter((offer) => !!offer).map((offer) => ({
  id: offer.id,
  status: offer.status ?? null,
  driver: offer.driver ? {
    id: offer.driver.id ?? null,
    display_name: offer.driver.user?.display_name ?? null,
    avatar_url: offer.driver.user?.avatar_url ?? null
  } : null,
  offered_at: offer.offered_at ?? null,
  offered_by: offer.offered_by ?? null,
  accepted_at: offer.accepted_at ?? null,
  accepted_by: offer.accepted_by ?? null,
  createdat: offer.createdat ?? null,
  createdby: offer.createdby ?? null,
  updatedat: offer.updatedat ?? null,
  updatedby: offer.updatedby ?? null
}));
var resolveMoveDetailsAssignment = (move) => {
  const scenario = findCurrentScenario(move);
  return resolveDriverAssignment({ legs: toLegInputs(scenario), offers: toOfferInputs(scenario) });
};
var OPEN_STATUSES = [null, `dispatched`, `pickup started`];
var resolveAssignBlock = (move) => {
  if (!move?.lite) return { code: "not_lite" };
  if (move?.status === void 0 || !OPEN_STATUSES.includes(move.status)) {
    return { code: "move_status", status: move?.status };
  }
  if (move?.parentMove && (move.parentMove.status === void 0 || !OPEN_STATUSES.includes(move.parentMove.status))) {
    return { code: "parent_status", status: move.parentMove.status };
  }
  const activeRideMove = move?.childMoves?.find((childMove) => childMove?.move_type === `ride` && childMove?.active === 1);
  if (activeRideMove?.status && !isRideRetractable(activeRideMove.status)) {
    return { code: "ride_status", status: activeRideMove.status };
  }
  return null;
};
var canAssignOrUnassignMove = (move) => resolveAssignBlock(move) === null;

// src/components/MoveDetailsOffer.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var MoveDetailsOffer = ({
  move,
  offers,
  isAssigningOrUnassigning,
  onAssignOrUnassign,
  readOnly,
  assignButtonStyle,
  rideshareHref,
  rideshareLinkComponent
}) => {
  const rideMove = move?.childMoves?.find((childMove) => childMove?.move_type === "ride" && childMove?.active);
  if (!move?.lite) return null;
  return /* @__PURE__ */ jsx2(
    RunDetailOffer_default,
    {
      offers,
      assignment: resolveMoveDetailsAssignment(move),
      canAssign: canAssignOrUnassignMove(move),
      isAssigningOrUnassigning,
      onAssignOrUnassign: readOnly ? void 0 : onAssignOrUnassign,
      assignButtonStyle,
      trailing: rideMove ? /* @__PURE__ */ jsx2(
        Button2,
        {
          size: "small",
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx2(BiCar, {}),
          component: rideshareLinkComponent,
          to: rideshareHref(rideMove.id),
          children: "View Rideshare"
        }
      ) : void 0
    }
  );
};
var MoveDetailsOffer_default = MoveDetailsOffer;

// src/components/AssignOrUnassignMoveModalContent.tsx
import React3 from "react";
import { toast } from "react-toastify";
import dayjs4 from "dayjs";
import utc from "dayjs/plugin/utc";

// src/lib/dayjsCeil.ts
var ceil = (option, dayjsClass) => {
  dayjsClass.prototype.ceil = function(amount, unit) {
    return this.add(amount - this.get(unit) % amount, unit).startOf(unit);
  };
};
var dayjsCeil_default = ceil;

// src/components/AssignOrUnassignMoveModalContent.tsx
import { logEvent } from "@hopdrive/sdk-events";
import { useReloadGuard } from "@hopdrive/sdk-app-updater/react";
import { css as css4, keyframes } from "@emotion/css";
import {
  useTheme as useTheme5,
  Avatar as Avatar3,
  Button as Button3,
  LinearProgress as LinearProgress2,
  Typography as Typography4,
  Tooltip as Tooltip2
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import {
  BiSolidCheckCircle,
  BiSolidInfoCircle,
  BiSolidLogInCircle,
  BiSolidUserCheck as BiSolidUserCheck2,
  BiSolidUserX as BiSolidUserX2
} from "react-icons/bi";
import { BiCar as BiCar2 } from "react-icons/bi";
import { MdDoNotDisturb } from "react-icons/md";

// src/components/DriverPicker.tsx
import React from "react";
import { css as css2 } from "@emotion/css";
import {
  Alert,
  Avatar as Avatar2,
  Badge,
  Chip as Chip2,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography as Typography2,
  useTheme as useTheme2
} from "@mui/material";
import { BiChevronRight, BiSearch, BiX } from "react-icons/bi";

// src/driverRanking.ts
import dayjs from "dayjs";

// src/lib/formatMoveId.ts
var formatMoveId = (moveId, options = {}) => {
  const { includeHash = true, isLite = false } = options;
  let str = ``;
  if (moveId) {
    if (isLite) str += `L-`;
    else if (includeHash) str += `#`;
    str += `${moveId}`;
  }
  if (!str) str = `-`;
  return str;
};

// src/driverRanking.ts
var AT_PICKUP_MILES = 0.1;
var formatDistanceToPickup = (miles) => miles < AT_PICKUP_MILES ? "At pickup location" : `${miles.toFixed(1)} mi away`;
var ATTRIBUTE_WORK_NAMES = {
  mobility_valet: "valet",
  mobility_service: "mobile service",
  mobility_parts: "parts delivery"
};
var driverRankGroup = (ranked) => {
  if (!ranked) return 2;
  const reason = ranked.ineligibility?.reason;
  if (reason === "missing_attribute" || reason === "no_schedule" || reason === "no_timezone") return 3;
  if (reason === "not_scored") return 2;
  if (reason === "outside_schedule" || ranked.status === "busy") return 1;
  return 0;
};
var TRAILING_TIMESTAMP = /(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?)\s*$/;
var formatConflictEnd = (window) => {
  if (!window?.trim()) return null;
  const matched = window.match(TRAILING_TIMESTAMP)?.[1];
  if (matched) {
    const parsed = dayjs(matched);
    if (parsed.isValid()) return parsed.format("h:mm A");
  }
  return window.trim();
};
var formatConflictInstant = (instant) => {
  if (!instant?.trim()) return null;
  const parsed = dayjs(instant);
  return parsed.isValid() ? parsed.format("h:mm A") : null;
};
var movesTodayPhrase = (movesOnDay) => movesOnDay <= 0 ? "No moves scheduled today" : `${movesOnDay} ${movesOnDay === 1 ? "move" : "moves"} scheduled today`;
var resolveDriverRow = ({
  status,
  ranked
}) => {
  const isOnline = status === "online";
  const isOffline = status === "offline";
  const onMove = !isOnline && !isOffline;
  const busy = ranked?.status === "busy";
  const chipLabel = isOffline ? "Offline" : onMove || busy ? "On a move" : "Online";
  const tone = isOffline ? "default" : onMove || busy ? "warning" : "success";
  const parts = [];
  const miles = ranked?.scoring?.distanceToPickup;
  if (typeof miles === "number" && Number.isFinite(miles)) {
    parts.push(formatDistanceToPickup(miles));
  } else if (ranked?.scoring?.positionUnknown) {
    parts.push("Location not set");
  }
  const reason = ranked?.ineligibility?.reason;
  if (reason === "missing_attribute") {
    const work = ATTRIBUTE_WORK_NAMES[ranked?.ineligibility?.requiredAttribute ?? ""];
    parts.push(work ? `Not set up for ${work} moves` : "Not set up for this kind of move");
  } else if (reason === "no_schedule") {
    parts.push("No schedule set");
  } else if (reason === "no_timezone") {
    parts.push("No timezone set");
  } else if (reason === "outside_schedule") {
    parts.push("Not scheduled at this time");
  } else if (reason === "conflicting_move" || busy && ranked?.conflict) {
    const conflicting = ranked?.ineligibility?.conflictingMove;
    const moveId = conflicting?.moveId ?? ranked?.conflict?.moveId;
    const end = conflicting?.end ? formatConflictInstant(conflicting.end) : formatConflictEnd(ranked?.conflict?.window);
    if (moveId != null) {
      const moveLabel = formatMoveId(moveId);
      parts.push(end ? `On ${moveLabel}, delivers ${end}` : `On ${moveLabel}`);
    }
  }
  if (ranked) parts.push(movesTodayPhrase(ranked.movesOnDay));
  return { chipLabel, chipTone: tone, dotTone: tone, meta: parts.join(" \xB7 ") };
};
var driverInitials = (displayName) => {
  const words = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join("");
};

// src/components/DriverPicker.tsx
import { Fragment as Fragment2, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var DriverPicker = ({
  drivers,
  ranked,
  loading,
  error,
  rankingLoading,
  rankingUnavailable,
  value,
  currentDriverId = 0,
  onChange,
  invalid
}) => {
  const theme = useTheme2();
  const cls = useStyles2(theme);
  const [search, setSearch] = React.useState("");
  const [mode, setMode] = React.useState("recommended");
  const [showUnavailable, setShowUnavailable] = React.useState(false);
  const effectiveMode = rankingUnavailable ? "alpha" : mode;
  const term = search.trim().toLowerCase();
  const visible = React.useMemo(() => {
    const matched = term ? drivers.filter((driver) => (driver.user?.display_name ?? "").toLowerCase().includes(term)) : [...drivers];
    const byName = (a, b) => (a.user?.display_name ?? "").localeCompare(b.user?.display_name ?? "");
    if (effectiveMode === "alpha") return matched.sort(byName);
    return matched.sort((a, b) => {
      const ra = ranked.get(a.id);
      const rb = ranked.get(b.id);
      const group = driverRankGroup(ra) - driverRankGroup(rb);
      if (group !== 0) return group;
      if ((rb?.finalScore ?? 0) !== (ra?.finalScore ?? 0)) return (rb?.finalScore ?? 0) - (ra?.finalScore ?? 0);
      return byName(a, b);
    });
  }, [drivers, ranked, term, effectiveMode]);
  const canSplit = effectiveMode === "recommended" && !rankingUnavailable && !term && ranked.size > 0 && visible.length > 0;
  const { available, unavailable } = React.useMemo(() => {
    if (!canSplit) return { available: visible, unavailable: [] };
    const recommendable = [];
    const ruledOut = [];
    for (const driver of visible) {
      const group = driverRankGroup(ranked.get(driver.id));
      (group === 1 || group === 3 ? ruledOut : recommendable).push(driver);
    }
    return { available: recommendable, unavailable: ruledOut };
  }, [canSplit, visible, ranked]);
  const selectedIsUnavailable = unavailable.some((driver) => driver.id === value);
  const unavailableOpen = showUnavailable || selectedIsUnavailable || available.length === 0;
  if (error) {
    return /* @__PURE__ */ jsx3(Alert, { severity: "error", children: "Could not load drivers. Close the dialog and try again." });
  }
  const renderRow = (driver) => {
    const row = resolveDriverRow({ status: driver.status, ranked: ranked.get(driver.id) });
    const selected = driver.id === value;
    const isCurrent = currentDriverId > 0 && driver.id === currentDriverId;
    const name = driver.user?.display_name ?? "Driver";
    const dotColor = row.dotTone === "success" ? theme?.palette?.success?.main : row.dotTone === "warning" ? theme?.palette?.warning?.main : theme?.palette?.grey?.[400];
    return /* @__PURE__ */ jsxs2(
      "div",
      {
        role: "option",
        "aria-label": name,
        "aria-selected": selected,
        tabIndex: 0,
        className: selected ? cls.rowSelected : cls.row,
        onClick: () => {
          if (!selected) onChange(driver.id);
        },
        onKeyDown: (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!selected) onChange(driver.id);
          }
        },
        children: [
          /* @__PURE__ */ jsx3(
            Badge,
            {
              sx: {
                "& .MuiBadge-badge": {
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  border: `2px solid ${theme?.palette?.background?.paper}`,
                  backgroundColor: dotColor
                }
              },
              overlap: "circular",
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              variant: "dot",
              children: /* @__PURE__ */ jsx3(Avatar2, { src: driver.user?.avatar_url || "", alt: name, children: driverInitials(name) })
            }
          ),
          /* @__PURE__ */ jsxs2("div", { className: cls.rowText, children: [
            /* @__PURE__ */ jsx3(Typography2, { variant: "body2", className: cls.name, children: name }),
            row.meta && /* @__PURE__ */ jsx3(Typography2, { variant: "caption", className: cls.meta, children: row.meta })
          ] }),
          isCurrent && /* @__PURE__ */ jsx3(Chip2, { size: "small", variant: "outlined", label: "Currently assigned", className: cls.chip }),
          /* @__PURE__ */ jsx3(Chip2, { size: "small", color: row.chipTone, label: row.chipLabel, className: cls.chip }),
          selected && /* @__PURE__ */ jsx3(Tooltip, { placement: "top", title: "Clear the selected driver", children: /* @__PURE__ */ jsx3(
            IconButton,
            {
              size: "small",
              "aria-label": `Clear selected driver ${name}`,
              className: cls.clear,
              onClick: (event) => {
                event.stopPropagation();
                onChange(0);
              },
              onKeyDown: (event) => event.stopPropagation(),
              children: /* @__PURE__ */ jsx3(BiX, { size: 18 })
            }
          ) })
        ]
      },
      driver.id
    );
  };
  return /* @__PURE__ */ jsxs2("div", { children: [
    /* @__PURE__ */ jsxs2("div", { className: cls.controls, children: [
      /* @__PURE__ */ jsx3(
        TextField,
        {
          fullWidth: true,
          size: "small",
          placeholder: "Search drivers",
          value: search,
          onChange: (event) => setSearch(event.target.value),
          slotProps: {
            input: {
              startAdornment: /* @__PURE__ */ jsx3(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx3(BiSearch, { size: 18 }) })
            }
          }
        }
      ),
      !rankingUnavailable && /* @__PURE__ */ jsxs2(
        ToggleButtonGroup,
        {
          exclusive: true,
          size: "small",
          value: mode,
          onChange: (_event, next) => next && setMode(next),
          "aria-label": "Driver order",
          children: [
            /* @__PURE__ */ jsx3(ToggleButton, { value: "recommended", "aria-label": "Recommended order", children: "Recommended" }),
            /* @__PURE__ */ jsx3(ToggleButton, { value: "alpha", "aria-label": "Alphabetical order", children: "A\u2013Z" })
          ]
        }
      )
    ] }),
    (loading || rankingLoading) && /* @__PURE__ */ jsx3(LinearProgress, { className: cls.progress }),
    invalid && /* @__PURE__ */ jsx3(Typography2, { variant: "caption", className: cls.invalid, children: "Please select a driver." }),
    /* @__PURE__ */ jsxs2("div", { className: cls.scroll, children: [
      !loading && !visible.length && /* @__PURE__ */ jsx3(Typography2, { variant: "body2", className: cls.empty, children: term ? "No drivers match that search." : "No drivers available for this move." }),
      !canSplit && visible.length > 0 && /* @__PURE__ */ jsx3("div", { className: cls.list, role: "listbox", "aria-label": "Drivers", children: visible.map(renderRow) }),
      canSplit && /* @__PURE__ */ jsxs2(Fragment2, { children: [
        available.length > 0 ? /* @__PURE__ */ jsx3("div", { className: cls.list, role: "listbox", "aria-label": "Recommended drivers", children: available.map(renderRow) }) : /* @__PURE__ */ jsx3(Typography2, { variant: "body2", className: cls.empty, children: "No recommended drivers for this move \u2014 every driver is unavailable or not set up for it." }),
        unavailable.length > 0 && /* @__PURE__ */ jsxs2(Fragment2, { children: [
          /* @__PURE__ */ jsxs2(
            "button",
            {
              type: "button",
              className: cls.toggle,
              "aria-expanded": unavailableOpen,
              onClick: () => setShowUnavailable((open) => !open),
              children: [
                /* @__PURE__ */ jsx3(
                  BiChevronRight,
                  {
                    size: 18,
                    className: unavailableOpen ? cls.chevronOpen : cls.chevron,
                    "aria-hidden": true
                  }
                ),
                unavailable.length,
                " unavailable ",
                unavailable.length === 1 ? "driver" : "drivers"
              ]
            }
          ),
          /* @__PURE__ */ jsx3(Collapse, { in: unavailableOpen, unmountOnExit: true, children: /* @__PURE__ */ jsx3("div", { className: cls.list, role: "listbox", "aria-label": "Unavailable drivers", children: unavailable.map(renderRow) }) })
        ] })
      ] })
    ] })
  ] });
};
var useStyles2 = (theme) => {
  const rowBase = css2`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background-color 120ms ease, border-color 120ms ease;

    &:hover {
      background-color: ${theme?.palette?.action?.hover};
    }

    &:focus-visible {
      outline: 2px solid ${theme?.palette?.primary?.main};
      outline-offset: -2px;
    }
  `;
  return {
    controls: css2`
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    `,
    progress: css2`
      margin-top: 8px;
      border-radius: 2px;
    `,
    invalid: css2`
      display: block;
      margin-top: 6px;
      color: ${theme?.palette?.error?.main};
    `,
    // The scroll container holds the recommended list, the drop-down toggle, and
    // the collapsed unavailable list, so the whole picker scrolls as one.
    scroll: css2`
      margin-top: 8px;
      max-height: 320px;
      overflow-y: auto;
    `,
    list: css2`
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    toggle: css2`
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      margin: 4px 0;
      padding: 8px 12px;
      border: none;
      background: none;
      border-radius: 8px;
      cursor: pointer;
      font: inherit;
      font-size: 0.8125rem;
      font-weight: 500;
      text-align: left;
      color: ${theme?.palette?.text?.secondary};

      &:hover {
        background-color: ${theme?.palette?.action?.hover};
      }

      &:focus-visible {
        outline: 2px solid ${theme?.palette?.primary?.main};
        outline-offset: -2px;
      }
    `,
    chevron: css2`
      flex: none;
      transition: transform 150ms ease;
    `,
    chevronOpen: css2`
      flex: none;
      transition: transform 150ms ease;
      transform: rotate(90deg);
    `,
    row: rowBase,
    rowSelected: css2`
      ${rowBase};
      background-color: ${theme?.palette?.primary?.main}14;
      border-color: ${theme?.palette?.primary?.main};

      &:hover {
        background-color: ${theme?.palette?.primary?.main}1f;
      }
    `,
    rowText: css2`
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    `,
    name: css2`
      font-weight: 500;
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `,
    // The secondary line can legitimately run long ("0.8 mi away · On #81742,
    // delivers 1:50 PM · 3 moves scheduled today"). It used to overflow its row and
    // get clipped mid-word with no ellipsis, so a reason could be cut to something
    // that read like a different sentence. Two lines, then ellipsise — bounded, and
    // never silently truncated.
    meta: css2`
      color: ${theme?.palette?.text?.secondary};
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      overflow-wrap: anywhere;
    `,
    chip: css2`
      flex: none;
    `,
    clear: css2`
      flex: none;
      margin-left: -4px;
      color: ${theme?.palette?.text?.secondary};
    `,
    empty: css2`
      padding: 16px 12px;
      color: ${theme?.palette?.text?.secondary};
    `
  };
};
var DriverPicker_default = DriverPicker;

// src/components/AssignOrUnassignMoveModalContent.tsx
import { isOfferAccepted as isOfferAccepted2 } from "@hopdrive/sdk-dispatch";

// src/useDriverRanking.ts
import React2 from "react";
var EMPTY = /* @__PURE__ */ new Map();
var useDriverRanking = ({
  driverIds,
  pickupTime,
  pickupLocation,
  pickupTimezone,
  moveDurationSec,
  excludeMoveIds,
  getRankedDrivers,
  onError,
  pending = false
}) => {
  const [ranked, setRanked] = React2.useState(EMPTY);
  const [loading, setLoading] = React2.useState(false);
  const [failed, setFailed] = React2.useState(false);
  const latitude = pickupLocation?.latitude;
  const longitude = pickupLocation?.longitude;
  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);
  const hasRankingFn = !!getRankedDrivers;
  const driverIdsKey = driverIds.join(",");
  const excludeKey = (excludeMoveIds ?? []).join(",");
  const getRankedDriversRef = React2.useRef(getRankedDrivers);
  const onErrorRef = React2.useRef(onError);
  React2.useEffect(() => {
    getRankedDriversRef.current = getRankedDrivers;
    onErrorRef.current = onError;
  }, [getRankedDrivers, onError]);
  React2.useEffect(() => {
    const fetchRanking = getRankedDriversRef.current;
    if (pending || !fetchRanking || !driverIdsKey || !pickupTime || !hasCoords) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const result = await fetchRanking({
          driverIds: driverIdsKey.split(",").map(Number),
          pickupTime,
          // The scorer wants minutes; the lane carries seconds. A missing duration
          // would make every conflict window collapse, so fall back to an hour.
          moveDurationMinutes: Math.round((moveDurationSec ?? 3600) / 60),
          pickupLocation: { latitude, longitude },
          pickupTimezone: pickupTimezone ?? null,
          moveType: "valet",
          excludeMoveIds: excludeKey ? excludeKey.split(",").map(Number) : []
        });
        if (cancelled) return;
        setRanked(new Map(result.map((entry) => [entry.driverId, entry])));
        setFailed(false);
      } catch (err) {
        if (cancelled) return;
        console.error("[useDriverRanking] Error:", err);
        onErrorRef.current?.(err);
        setRanked(EMPTY);
        setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [pending, hasRankingFn, driverIdsKey, excludeKey, pickupTime, hasCoords, latitude, longitude, moveDurationSec, pickupTimezone]);
  if (pending) return { ranked, loading: hasRankingFn, unavailable: !hasRankingFn };
  return {
    ranked,
    loading,
    unavailable: !getRankedDrivers || !hasCoords || failed
  };
};

// src/internal/ModalComponents.tsx
import { Box as Box2, DialogActions, DialogContent, DialogTitle, Icon, IconButton as IconButton2, Typography as Typography3 } from "@mui/material";
import { useTheme as useTheme3 } from "@mui/material/styles";
import { Fragment as Fragment3, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var ModalHeader = ({ handleClose, children }) => {
  const theme = useTheme3();
  return /* @__PURE__ */ jsxs3(
    "span",
    {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1, 1, 2)
      },
      children: [
        /* @__PURE__ */ jsx4(DialogTitle, { sx: { padding: 0, margin: 0, lineHeight: 1.2, fontSize: "20px", fontWeight: 600 }, children }),
        handleClose && /* @__PURE__ */ jsx4(
          IconButton2,
          {
            sx: { padding: theme.spacing(1), color: theme.palette.text.primary },
            onClick: handleClose,
            "aria-label": "close",
            children: /* @__PURE__ */ jsx4(Icon, { children: "close" })
          }
        )
      ]
    }
  );
};
var ModalContent = ({ subtitle, disableDividers = false, children }) => {
  const theme = useTheme3();
  const sxStyles = {
    padding: { padding: theme.spacing(2), margin: 0, display: "block", overflowY: "auto" },
    subtitle: { color: theme.palette.text.secondary, fontSize: "14px", fontWeight: 400 },
    break: { width: "100%", height: theme.spacing(2) }
  };
  return /* @__PURE__ */ jsxs3(DialogContent, { dividers: !disableDividers, sx: sxStyles.padding, children: [
    subtitle && /* @__PURE__ */ jsxs3(Fragment3, { children: [
      /* @__PURE__ */ jsx4(Typography3, { sx: sxStyles.subtitle, children: subtitle }),
      /* @__PURE__ */ jsx4(Box2, { sx: sxStyles.break })
    ] }),
    children
  ] });
};
var ModalFooter = ({ children }) => {
  const theme = useTheme3();
  return /* @__PURE__ */ jsx4(
    DialogActions,
    {
      sx: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        margin: 0,
        background: theme.palette.grey[50]
      },
      children
    }
  );
};

// src/internal/Spacer.tsx
import { useTheme as useTheme4 } from "@mui/material/styles";
import { css as css3 } from "@emotion/css";
import { jsx as jsx5 } from "react/jsx-runtime";
var sizeMapping = {
  xxxs: 0.25,
  xxs: 0.5,
  xs: 1,
  sm: 1.5,
  md: 2,
  lg: 3,
  xl: 4,
  xxl: 6,
  xxxl: 8
};
var getSize = (size) => {
  if (typeof size === "string") {
    const parsedSize = parseFloat(size);
    if (!isNaN(parsedSize)) {
      return parsedSize;
    }
    return sizeMapping[size] || 2;
  }
  return size || 2;
};
var useStyles3 = (theme, size) => ({
  spacerH: css3`
    display: 'block';
    width: '100%';
    height: ${theme.spacing(size)};
  `,
  spacerV: css3`
    display: 'inline-block';
    width: ${theme.spacing(size)};
  `
});
var Spacer = ({ column, size }) => {
  const theme = useTheme4();
  const styles = useStyles3(theme, getSize(size));
  return /* @__PURE__ */ jsx5("div", { className: column ? styles.spacerV : styles.spacerH });
};
var Spacer_default = Spacer;

// src/lib/objectDiff.ts
var getObjectDiff = (oldObj = {}, newObj = {}, options = {}) => {
  const { depth = 2, skipKeys = [], keysFrom = "both" } = options;
  const oldFields = {};
  const newFields = {};
  const isPlainObject = (val) => val !== null && typeof val === "object" && !Array.isArray(val);
  const allKeys = keysFrom === "old" ? Object.keys(oldObj || {}) : keysFrom === "new" ? Object.keys(newObj || {}) : [.../* @__PURE__ */ new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})])];
  for (const key of allKeys) {
    if (skipKeys.includes(key)) continue;
    const oldVal = (oldObj || {})[key] ?? null;
    const newVal = (newObj || {})[key] ?? null;
    if (depth > 0 && isPlainObject(oldVal) && isPlainObject(newVal)) {
      const nestedDiff = getObjectDiff(oldVal, newVal, { depth: depth - 1, skipKeys, keysFrom });
      if (nestedDiff) {
        oldFields[key] = nestedDiff.old;
        newFields[key] = nestedDiff.new;
      }
    } else {
      const oldNorm = oldVal != null ? JSON.stringify(oldVal) : null;
      const newNorm = newVal != null ? JSON.stringify(newVal) : null;
      if (oldNorm !== newNorm) {
        oldFields[key] = oldVal;
        newFields[key] = newVal;
      }
    }
  }
  if (Object.keys(oldFields).length === 0) return null;
  return { old: oldFields, new: newFields };
};

// src/assignModalHelpers.ts
import dayjs2 from "dayjs";
dayjs2.extend(dayjsCeil_default);
var getDefaultPickupDate = (move) => {
  const currentDate = dayjs2().ceil(5, `minute`);
  const readyDate = dayjs2(move?.ready_by || move?.pickup_time);
  if (!readyDate.isValid() || readyDate.isBefore(currentDate)) return currentDate.format(`YYYY-MM-DD`);
  return readyDate.format(`YYYY-MM-DD`);
};
var getDefaultPickupTime = (move) => {
  const currentTime = dayjs2().ceil(5, `minute`);
  const readyTime = dayjs2(move?.ready_by || move?.pickup_time);
  if (!readyTime.isValid() || readyTime.isBefore(currentTime)) return currentTime.format(`HH:mm`);
  return readyTime.format(`HH:mm`);
};
var isRideCallingDisabled = (effectiveConfig) => {
  return !effectiveConfig?.rideshare_calling_enabled;
};
var resolveCurrentDriverId = ({
  moveDriverId,
  offers
}) => {
  if (moveDriverId) return moveDriverId;
  const accepted = offers?.find((offer) => offer?.status === "assigned" || offer?.status === "accepted");
  return accepted?.driver?.id ?? offers?.[0]?.driver?.id ?? 0;
};
var resolveAssignFooterDisabled = ({
  driverId,
  currentDriverId = 0,
  isSaving
}) => {
  if (isSaving) {
    return { reassign: { disabled: true, reason: "" }, unassign: { disabled: true, reason: "" } };
  }
  const selectedIsCurrent = driverId > 0 && driverId === currentDriverId;
  const hasReplacement = driverId > 0 && !selectedIsCurrent;
  if (hasReplacement) {
    return {
      reassign: { disabled: false, reason: "" },
      unassign: { disabled: true, reason: "Clear the selected driver to unassign this move instead." }
    };
  }
  return {
    reassign: {
      disabled: true,
      reason: selectedIsCurrent ? "This driver is already assigned to the move. Pick a different driver to reassign it." : "Select a replacement driver to reassign this move."
    },
    unassign: { disabled: false, reason: "" }
  };
};
var isConciergeMove = (move) => {
  const usecaseKey = move?.usecase_key;
  return usecaseKey === "concierge-ret" || usecaseKey === "concierge-pu";
};
var isConciergePickupType = (move) => {
  const key = move?.usecase_key;
  return key === "concierge-pu" || key === "concierge-loaner-pu";
};

// src/moveDispatchService.ts
import axios from "axios";
import dayjs3 from "dayjs";
var log = false;
var convertMoveToMoveGroup = (moveData) => ({
  id: moveData.id,
  move_type: moveData.move_type || "",
  usecase_key: moveData.usecase_key,
  pickup_time: moveData.pickup_time,
  delivery_time: moveData.delivery_time,
  parent_move_id: moveData.parent_move_id,
  active: moveData.active !== void 0 ? Boolean(moveData.active) : true,
  lite: moveData.lite || false,
  cancel_status: moveData.cancel_status
});
var calculateMovePairTimes = (selectedTime, usecaseKey, parentDurationSec) => {
  const base = dayjs3(selectedTime);
  const isConciergeLoanerPu = usecaseKey === "concierge-loaner-pu";
  if (isConciergeLoanerPu) {
    const parentPickup2 = base.subtract(parentDurationSec, "second").toISOString();
    const parentDelivery2 = base.toISOString();
    const childPickup2 = base.toISOString();
    const childDelivery2 = base.add(parentDurationSec, "second").toISOString();
    return { parentPickup: parentPickup2, parentDelivery: parentDelivery2, childPickup: childPickup2, childDelivery: childDelivery2 };
  }
  const parentPickup = base.toISOString();
  const parentDelivery = base.add(parentDurationSec, "second").toISOString();
  const childPickup = base.add(parentDurationSec, "second").toISOString();
  const childDelivery = base.add(parentDurationSec * 2, "second").toISOString();
  return { parentPickup, parentDelivery, childPickup, childDelivery };
};
var PAIRED_USECASES = /* @__PURE__ */ new Set(["concierge-loaner-pu", "concierge-loaner-ret", "round-trip"]);
var resolveMoveDispatchTimes = (moveData, selectedTime) => {
  const duration = moveData.lane?.duration_sec || 0;
  const usecaseKey = moveData.usecase_key;
  const isParentMove = !moveData.parent_move_id;
  const childDriveMoves = (moveData.childMoves ?? []).filter(
    (child) => !!child && child.move_type === "drive" && child.active === 1
  );
  const isMovePair = !!usecaseKey && PAIRED_USECASES.has(usecaseKey);
  if (isMovePair && (childDriveMoves.length > 0 || moveData.parentMove)) {
    const parentDuration = isParentMove ? duration : moveData.parentMove?.lane?.duration_sec || duration;
    const pairTimes = calculateMovePairTimes(selectedTime, usecaseKey, parentDuration);
    return {
      pickupTime: isParentMove ? pairTimes.parentPickup : pairTimes.childPickup,
      deliveryTime: isParentMove ? pairTimes.parentDelivery : pairTimes.childDelivery,
      pairTimes,
      isParentMove,
      childDriveMoves
    };
  }
  return {
    pickupTime: selectedTime,
    deliveryTime: dayjs3(selectedTime).add(duration, "second").toISOString(),
    pairTimes: null,
    isParentMove,
    childDriveMoves
  };
};
var ensureDeliverBy = (currentDeliverBy, newReadyBy) => {
  const minDeliverBy = dayjs3(newReadyBy).add(1, "hour").toISOString();
  if (currentDeliverBy && !dayjs3(currentDeliverBy).isBefore(minDeliverBy)) {
    return currentDeliverBy;
  }
  return minDeliverBy;
};
var dispatchLiteMove = async (moveData, targetDriverId, pickupTime, action, user, updateMoveTimes, functionsOrigin, withRideshare) => {
  if (!moveData) {
    throw new Error("Move data is required");
  }
  if (!targetDriverId) {
    throw new Error("Target driver ID is required");
  }
  if (!pickupTime) {
    throw new Error("Pickup time is required");
  }
  if (!moveData.lane?.duration_sec) {
    throw new Error("Move lane duration is missing - cannot calculate delivery time");
  }
  log && console.log(`[dispatchLiteMove] Starting dispatch for move ${moveData.id} to driver ${targetDriverId}`);
  const { pickupTime: actualPickupTime, deliveryTime: actualDeliveryTime, pairTimes: times, isParentMove, childDriveMoves } = resolveMoveDispatchTimes(moveData, pickupTime);
  if (times) {
    log && console.log(`[dispatchLiteMove] Move pair detected (${moveData.usecase_key}). Calculated times:`, times);
    if (isParentMove) {
      for (const childMove of childDriveMoves) {
        log && console.log(`[dispatchLiteMove] Updating child drive move ${childMove.id} times: pickup=${times.childPickup}, delivery=${times.childDelivery}`);
        await updateMoveTimes({
          variables: {
            moveId: childMove.id,
            pickupTime: times.childPickup,
            deliveryTime: times.childDelivery,
            readyBy: times.childPickup,
            deliverBy: ensureDeliverBy(childMove.deliver_by, times.childPickup)
          }
        });
      }
    } else if (moveData.parentMove) {
      log && console.log(`[dispatchLiteMove] Updating parent move ${moveData.parentMove.id} times: pickup=${times.parentPickup}, delivery=${times.parentDelivery}`);
      await updateMoveTimes({
        variables: {
          moveId: moveData.parentMove.id,
          pickupTime: times.parentPickup,
          deliveryTime: times.parentDelivery,
          readyBy: times.parentPickup,
          deliverBy: ensureDeliverBy(moveData.parentMove.deliver_by, times.parentPickup)
        }
      });
    }
  }
  log && console.log(`[dispatchLiteMove] Primary move times: pickup=${actualPickupTime}, delivery=${actualDeliveryTime}, duration=${moveData.lane.duration_sec}s`);
  await updateMoveTimes({
    variables: {
      moveId: moveData.id,
      pickupTime: actualPickupTime,
      deliveryTime: actualDeliveryTime,
      readyBy: actualPickupTime,
      deliverBy: ensureDeliverBy(moveData.deliver_by, actualPickupTime)
    }
  });
  const allMoves = [];
  allMoves.push(convertMoveToMoveGroup(moveData));
  if (moveData.childMoves) {
    log && console.log(`[dispatchLiteMove] Found ${moveData.childMoves.length} child moves for move ${moveData.id}`);
    moveData.childMoves.forEach((childMove) => {
      if (!childMove) return;
      log && console.log(`[dispatchLiteMove] Including child move ${childMove.id} (type: ${childMove.move_type}, active: ${childMove.active})`);
      allMoves.push(convertMoveToMoveGroup(childMove));
    });
  } else {
    log && console.log(`[dispatchLiteMove] No child moves found for move ${moveData.id}`);
  }
  if (moveData.parentMove) {
    allMoves.push(convertMoveToMoveGroup(moveData.parentMove));
  }
  const token = await user?.getToken();
  if (!token) {
    throw new Error("Failed to get authentication token");
  }
  const response = await axios({
    method: "POST",
    url: `${functionsOrigin}/.netlify/functions/dispatchMove`,
    data: {
      moveId: moveData.id,
      targetDriverId,
      pickupTime: actualPickupTime,
      deliveryTime: actualDeliveryTime,
      moveData: convertMoveToMoveGroup(moveData),
      allMoves,
      shouldUndispatchFirst: action === "reassign",
      withRideshare
    },
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (!response.data || !response.data.success) {
    throw new Error(response.data?.error || "Failed to dispatch move");
  }
  log && console.log(`[dispatchLiteMove] Success:`, response.data);
  return response.data;
};
var refetchMoveWithRelationships = async (moveId, getMoveForDispatch) => {
  try {
    log && console.log(`[refetchMove] Refetching move ${moveId} with updated relationships...`);
    const moveDetailsResult = await getMoveForDispatch({
      variables: { moveId }
    });
    if (moveDetailsResult.data?.moves_by_pk) {
      const refreshedMove = moveDetailsResult.data.moves_by_pk;
      log && console.log(`[refetchMove] Successfully refetched move. Child moves: ${refreshedMove.childMoves?.length || 0}`);
      return refreshedMove;
    } else {
      console.warn(`[refetchMove] Failed to refetch move data for move ${moveId}`);
      return null;
    }
  } catch (error) {
    console.error(`[refetchMove] Error refetching move ${moveId}:`, error);
    return null;
  }
};
var undispatchLiteMove = async (moveData, user, functionsOrigin) => {
  if (!moveData) {
    throw new Error("Move data is required");
  }
  log && console.log(`[undispatchLiteMove] Starting undispatch for move ${moveData.id}`);
  const allMoveIds = [moveData.id];
  if (moveData.childMoves) {
    moveData.childMoves.forEach((childMove) => {
      if (childMove) allMoveIds.push(childMove.id);
    });
  }
  if (moveData.parentMove) {
    allMoveIds.push(moveData.parentMove.id);
  }
  log && console.log(`[undispatchLiteMove] Processing ${allMoveIds.length} moves: ${allMoveIds.join(", ")}`);
  const token = await user?.getToken();
  if (!token) {
    throw new Error("Failed to get authentication token");
  }
  const response = await axios({
    method: "POST",
    url: `${functionsOrigin}/.netlify/functions/undispatchMove`,
    data: {
      moveId: moveData.id,
      allMoveIds
    },
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  if (!response.data || !response.data.success) {
    throw new Error(response.data?.error || "Failed to undispatch move");
  }
  log && console.log("[undispatchLiteMove] Success:", response.data);
  return response.data;
};

// src/components/AssignOrUnassignMoveModalContent.tsx
import { Fragment as Fragment4, jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
dayjs4.extend(utc);
dayjs4.extend(dayjsCeil_default);
var log2 = false;
var AssignOrUnassignMoveModalContent = ({ onClose, move, offers, host }) => {
  const theme = useTheme5();
  const cls = useStyles4(theme);
  const {
    user,
    functionsOrigin,
    userEmail,
    hasRideshareFeature,
    rideshareEnabled,
    effectiveConfig,
    effectiveConfigLoading,
    getDriversToAssign,
    getRankedDrivers,
    getMoveForDispatch,
    updateMoveTimes,
    onError
  } = host;
  const rideCallingDisabled = isRideCallingDisabled(effectiveConfig);
  const rideshareControlsDisabled = rideCallingDisabled || effectiveConfigLoading;
  const formState = move?.driver_id || offers?.length ? `unassign` : `assign`;
  const [drivers, setDrivers] = React3.useState([]);
  const [driversLoading, setDriversLoading] = React3.useState(true);
  const [driversError, setDriversError] = React3.useState(false);
  const [validation, setValidation] = React3.useState({
    pickupDate: true,
    pickupTime: true,
    driverId: true
  });
  const [isSaving, setIsSaving] = React3.useState(false);
  useReloadGuard(isSaving, "Driver assignment");
  const [pickupDate, setPickupDate] = React3.useState(getDefaultPickupDate(move));
  const [pickupTime, setPickupTime] = React3.useState(getDefaultPickupTime(move));
  const [pickupTimestamp, setPickupTimestamp] = React3.useState(
    move?.ready_by || move?.pickup_time || dayjs4().utc().format()
  );
  const [driverId, setDriverId] = React3.useState(0);
  const [createRideForReturn, setCreateRideForReturn] = React3.useState(false);
  const currentDriverId = resolveCurrentDriverId({ moveDriverId: move?.driver_id, offers });
  const currentOffer = offers?.find(isOfferAccepted2) ?? offers?.[0];
  const footerDisabled = resolveAssignFooterDisabled({ driverId, currentDriverId, isSaving });
  const isLoanerParent = move?.usecase_key === "concierge-loaner-pu" && !move?.parent_move_id;
  const [childAppointmentTime, setChildAppointmentTime] = React3.useState(null);
  React3.useEffect(() => {
    setCreateRideForReturn(!rideCallingDisabled && hasRideshareFeature);
  }, [rideCallingDisabled, hasRideshareFeature]);
  React3.useEffect(() => {
    const fetchDrivers = async () => {
      setDriversLoading(true);
      try {
        setDrivers(await getDriversToAssign());
        setDriversError(false);
      } catch (err) {
        console.error("[fetchDrivers] Error:", err);
        setDrivers([]);
        setDriversError(true);
      }
      setDriversLoading(false);
    };
    fetchDrivers();
  }, [move?.customer_id]);
  const [dispatchMove, setDispatchMove] = React3.useState(null);
  const [dispatchMoveSettled, setDispatchMoveSettled] = React3.useState(false);
  React3.useEffect(() => {
    if (!move?.id) {
      setDispatchMoveSettled(true);
      return;
    }
    let cancelled = false;
    Promise.resolve().then(() => getMoveForDispatch({ variables: { moveId: move.id } })).then((res) => {
      const moveData = res?.data?.moves_by_pk;
      if (!cancelled && moveData) setDispatchMove(moveData);
    }).catch((err) => {
      console.error("[fetchDispatchMove] Error:", err);
    }).finally(() => {
      if (!cancelled) setDispatchMoveSettled(true);
    });
    return () => {
      cancelled = true;
    };
  }, [move?.id]);
  const rankingMove = dispatchMove ?? move;
  const [childAppointmentSettled, setChildAppointmentSettled] = React3.useState(false);
  const rankingInputsReady = dispatchMoveSettled && (!isLoanerParent || !dispatchMove || childAppointmentSettled);
  const pickupLatitude = Number(rankingMove?.lane?.pickup?.latitude ?? NaN);
  const pickupLongitude = Number(rankingMove?.lane?.pickup?.longitude ?? NaN);
  const rankingPickupTime = React3.useMemo(() => {
    if (!pickupTimestamp || !rankingMove) return pickupTimestamp;
    return resolveMoveDispatchTimes(rankingMove, pickupTimestamp).pickupTime;
  }, [rankingMove, pickupTimestamp]);
  const {
    ranked,
    loading: rankingLoading,
    unavailable: rankingUnavailable
  } = useDriverRanking({
    driverIds: drivers.map((driver) => driver.id),
    pickupTime: rankingPickupTime,
    pickupLocation: { latitude: pickupLatitude, longitude: pickupLongitude },
    pickupTimezone: rankingMove?.lane?.pickup?.timezone ?? null,
    moveDurationSec: rankingMove?.lane?.duration_sec,
    // The whole order: the drive, its parent, every child (rides included) and, for a
    // child leg, the parent's other children (its ride leg). A targeted dispatch stamps
    // driver_id across the scenario, so without this a reassign ranked the current
    // driver against the very order in this modal.
    excludeMoveIds: [
      rankingMove?.id,
      rankingMove?.parent_move_id,
      rankingMove?.parentMove?.id,
      ...rankingMove?.childMoves?.map((child) => child?.id) ?? [],
      ...rankingMove?.parentMove?.childMoves?.map((child) => child?.id) ?? []
    ].filter((id, index, ids) => typeof id === "number" && ids.indexOf(id) === index),
    getRankedDrivers,
    onError,
    // While the move lookup (and a loaner parent's appointment step) is pending the
    // picker shows ranking-in-progress, not the unranked A–Z fallback it would show for
    // a move that genuinely can't be ranked.
    pending: !rankingInputsReady
  });
  const childAppointmentApplied = React3.useRef(false);
  React3.useEffect(() => {
    if (!isLoanerParent || !dispatchMove || childAppointmentApplied.current) return;
    childAppointmentApplied.current = true;
    setChildAppointmentSettled(true);
    const childDriveMove = dispatchMove.childMoves?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (child) => child?.move_type === "drive" && child?.active === 1
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    );
    if (childDriveMove?.ready_by) {
      const appointmentTime = childDriveMove.ready_by;
      setChildAppointmentTime(appointmentTime);
      const currentTime = dayjs4().ceil(5, "minute");
      const readyDate = dayjs4(appointmentTime);
      const formTime = readyDate.isBefore(currentTime) ? currentTime : readyDate;
      const nextDate = formTime.format("YYYY-MM-DD");
      const nextTime = formTime.format("HH:mm");
      setPickupDate(nextDate);
      setPickupTime(nextTime);
      setPickupTimestamp(dayjs4(`${nextDate} ${nextTime}`).toISOString());
    }
  }, [isLoanerParent, dispatchMove]);
  React3.useEffect(() => {
    if (pickupDate && pickupTime) {
      const selectedDateTime = dayjs4(`${pickupDate} ${pickupTime}`);
      const currentDate = dayjs4().format("YYYY-MM-DD");
      if (pickupDate === currentDate) {
        const minDateTime = dayjs4().add(1, "minute");
        if (selectedDateTime.isBefore(minDateTime)) {
          setPickupTime(dayjs4().add(5, "minute").format("HH:mm"));
        }
      } else if (pickupDate < currentDate) {
        setPickupTime(dayjs4().add(1, "minute").format("HH:mm"));
      }
    }
  }, [pickupDate]);
  React3.useEffect(() => {
    if (pickupDate && pickupTime) {
      const combinedTimestamp = dayjs4(`${pickupDate} ${pickupTime}`).toISOString();
      setPickupTimestamp(combinedTimestamp);
      log2 && console.log("[pickupTimestamp] Updated to:", combinedTimestamp);
    }
  }, [pickupDate, pickupTime]);
  const dispatchLiteMove2 = async (moveData, targetDriverId, pickupTimeArg, action, withRideshare) => {
    return dispatchLiteMove(moveData, targetDriverId, pickupTimeArg, action, user, updateMoveTimes, functionsOrigin, withRideshare);
  };
  const refetchMoveWithRelationships2 = async (moveId) => {
    return refetchMoveWithRelationships(moveId, getMoveForDispatch);
  };
  const undispatchLiteMove2 = async (moveData) => {
    return undispatchLiteMove(moveData, user, functionsOrigin);
  };
  const handleValidation = (action) => {
    const newValidation = {
      pickupDate: true,
      pickupTime: true,
      driverId: true
    };
    if (action !== `unassign`) {
      if (!pickupDate || pickupDate < dayjs4().format(`YYYY-MM-DD`)) {
        newValidation.pickupDate = false;
      }
      if (!pickupTime) {
        newValidation.pickupTime = false;
      } else {
        const selectedDateTime = dayjs4(`${pickupDate} ${pickupTime}`);
        const currentDate = dayjs4().format("YYYY-MM-DD");
        if (pickupDate === currentDate) {
          const minDateTime = dayjs4().add(1, `minute`);
          if (selectedDateTime.isBefore(minDateTime)) {
            newValidation.pickupTime = false;
          }
        } else if (pickupDate < currentDate) {
          newValidation.pickupTime = false;
        }
      }
      if (!driverId) {
        newValidation.driverId = false;
      }
    }
    setValidation(newValidation);
    return newValidation;
  };
  const handleSubmit = async (action) => {
    setIsSaving(true);
    const validationResult = handleValidation(action);
    const isValid = Object.values(validationResult).every(Boolean);
    if (!isValid) {
      if (!validationResult.pickupDate) {
        toast.warning("Invalid pickup date", { toastId: `pickup-date-warning` });
      }
      if (!validationResult.pickupTime) {
        toast.warning("Invalid pickup time", { toastId: `pickup-time-warning` });
      }
      if (!validationResult.driverId) {
        toast.warning("Please select a driver", { toastId: `driver-warning` });
      }
      setIsSaving(false);
      return;
    }
    let refreshedMoveData = move;
    try {
      if (action === "assign" || action === "reassign") {
        const refetchedMove = await refetchMoveWithRelationships2(move?.id || 0);
        if (refetchedMove) {
          refreshedMoveData = refetchedMove;
          log2 && console.log(`[refetch] Refetched move ${refreshedMoveData.id} with ${refreshedMoveData.childMoves?.length || 0} child moves`);
        } else {
          console.warn(`[refetch] Failed to refetch move ${move?.id}, using original move data`);
        }
      }
      if (!refreshedMoveData) {
        throw new Error("Move data is required");
      }
      if (action === "assign" || action === "reassign") {
        try {
          const result = await dispatchLiteMove2(refreshedMoveData, driverId, pickupTimestamp, action, createRideForReturn);
          log2 && console.log(`[dispatchLiteMove] Success:`, result);
          try {
            const { pickupTime: newPickupTime, deliveryTime: newDeliveryTime } = resolveMoveDispatchTimes(
              refreshedMoveData,
              pickupTimestamp
            );
            const newReadyBy = newPickupTime;
            const newDeliverBy = ensureDeliverBy(refreshedMoveData.deliver_by, newPickupTime);
            const selectedDriver = drivers.find((d) => d.id === driverId);
            const oldValues = {
              pickup_time: refreshedMoveData.pickup_time || null,
              delivery_time: refreshedMoveData.delivery_time || null,
              ready_by: refreshedMoveData.ready_by || null,
              deliver_by: refreshedMoveData.deliver_by || null,
              driver_id: refreshedMoveData.driver_id || null,
              driver_name: refreshedMoveData.driver?.user?.display_name || null
            };
            const newValues = {
              pickup_time: newPickupTime,
              delivery_time: newDeliveryTime,
              ready_by: newReadyBy,
              deliver_by: newDeliverBy,
              driver_id: driverId,
              driver_name: selectedDriver?.user?.display_name || null
            };
            const metadata = getObjectDiff(oldValues, newValues);
            if (metadata) {
              const eventRes = await logEvent("move.lite.assigned", {
                user: userEmail || "dealer",
                role: "dealer",
                move_id: refreshedMoveData.id,
                customer_id: refreshedMoveData.customer_id ?? void 0,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                lane_id: refreshedMoveData.lane?.id ?? void 0,
                driver_id: driverId,
                metadata,
                safeWithResponse: true
              });
              if (!eventRes.success) {
                console.error(`[logEvent] Failed to log move.lite.assigned for Move #${refreshedMoveData?.id}:`, eventRes.error);
              }
            }
          } catch (eventErr) {
            console.error(`[logEvent] Unexpected error logging move.lite.assigned for Move #${refreshedMoveData?.id}:`, eventErr);
          }
        } catch (dispatchError) {
          onError?.(dispatchError);
          console.error(`[dispatchLiteMove] Error for Move #${refreshedMoveData?.id}:`, dispatchError);
          throw new Error(`Failed to dispatch move: ${dispatchError.message}`);
        }
      } else if (action === "unassign") {
        try {
          const result = await undispatchLiteMove2(refreshedMoveData);
          log2 && console.log(`[undispatchLiteMove] Success:`, result);
        } catch (undispatchError) {
          onError?.(undispatchError);
          console.error(`[undispatchLiteMove] Error for Move #${refreshedMoveData?.id}:`, undispatchError);
          throw new Error(`Failed to undispatch move: ${undispatchError.message}`);
        }
      }
      log2 && console.log(`Successfully ${action}ed driver for Move #${refreshedMoveData?.id}.`);
      toast.success(`Successfully ${action}ed driver for Move #${refreshedMoveData?.id}.`, {
        toastId: `assign-unassign-move-success`
      });
      handleClose({
        action,
        driverId: action === "assign" || action === "reassign" ? driverId : null,
        moveId: refreshedMoveData?.id
      });
    } catch (err) {
      onError?.(err);
      console.error(`Failed to ${action} driver for Move #${refreshedMoveData?.id || move?.id}:`, err);
      let errorMessage = `Failed to ${action} driver for Move #${refreshedMoveData?.id || move?.id}.`;
      if (err.message) {
        errorMessage = `Failed to ${action} driver: ${err.message}`;
      }
      toast.error(errorMessage, {
        toastId: `assign-unassign-move-error`,
        autoClose: 8e3
        // Longer duration for error messages
      });
    }
    setIsSaving(false);
  };
  const handleClose = (output) => {
    if (onClose) onClose(output);
  };
  const getValidTimeForDate = (date) => {
    const selectedDate = dayjs4(date);
    const currentDate = dayjs4();
    if (selectedDate.isSame(currentDate, "day")) {
      return dayjs4().add(5, "minute").format("HH:mm");
    } else if (selectedDate.isAfter(currentDate, "day")) {
      return "09:00";
    } else {
      return dayjs4().add(1, "minute").format("HH:mm");
    }
  };
  const handleDateChange = (date) => {
    const newDate = date?.format(`YYYY-MM-DD`) || ``;
    setPickupDate(newDate);
    if (newDate) {
      const newTime = getValidTimeForDate(newDate);
      setPickupTime(newTime);
    }
  };
  return /* @__PURE__ */ jsxs4(Fragment4, { children: [
    /* @__PURE__ */ jsxs4(ModalHeader, { handleClose, children: [
      formState === "assign" ? `Assign` : `Unassign`,
      " Driver (Move",
      " ",
      formatMoveId(move?.id || 0, { isLite: !!move?.lite }),
      ")"
    ] }),
    /* @__PURE__ */ jsxs4(
      ModalContent,
      {
        subtitle: formState === `assign` ? isConciergePickupType(move) ? `Select an appointment date & time, then select a driver to assign to this move.` : `Select a ready by date & time, then select a driver to assign to this move.` : `By unassigning a driver, they will no longer have access to this move in their app. You can also reassign a different driver to this move by filling out the form below. Here is the driver that is currently assigned:`,
        children: [
          formState === `unassign` ? /* @__PURE__ */ jsxs4(Fragment4, { children: [
            /* @__PURE__ */ jsx6(
              Tooltip2,
              {
                placement: "top",
                title: isOfferAccepted2(currentOffer) ? `Accepted by Driver` : `Offered to Driver`,
                children: /* @__PURE__ */ jsxs4("div", { className: cls.driver, children: [
                  /* @__PURE__ */ jsx6(Avatar3, { src: move?.driver?.user?.avatar_url || currentOffer?.driver?.user?.avatar_url || "" }),
                  /* @__PURE__ */ jsx6(Typography4, { sx: { fontWeight: 600 }, children: move?.driver?.user?.display_name || currentOffer?.driver?.user?.display_name }),
                  isOfferAccepted2(currentOffer) ? /* @__PURE__ */ jsx6(BiSolidCheckCircle, { color: theme?.palette?.success?.main, size: 20 }) : /* @__PURE__ */ jsx6(BiSolidLogInCircle, { color: theme?.palette?.info?.main, size: 20 }),
                  /* @__PURE__ */ jsx6("div", { className: cls.driverShadow })
                ] })
              }
            ),
            /* @__PURE__ */ jsx6(Spacer_default, {})
          ] }) : null,
          formState === `unassign` ? /* @__PURE__ */ jsx6(Typography4, { sx: { mt: theme?.spacing(2), mb: theme?.spacing(1), fontSize: `20px`, fontWeight: 600 }, children: "Reassign Driver" }) : null,
          (() => {
            const infoTime = childAppointmentTime || move?.ready_by || move?.pickup_time;
            if (!infoTime) return null;
            return /* @__PURE__ */ jsxs4(Fragment4, { children: [
              /* @__PURE__ */ jsxs4("div", { className: cls.title, children: [
                /* @__PURE__ */ jsx6(BiSolidInfoCircle, { className: cls.titleIcon }),
                /* @__PURE__ */ jsx6(Typography4, { variant: "body2", sx: { textWrap: `pretty`, color: theme?.palette?.text?.secondary }, children: isConciergePickupType(move) ? /* @__PURE__ */ jsxs4(Fragment4, { children: [
                  "The appointment for this move ",
                  dayjs4(infoTime).isBefore(dayjs4()) ? `was` : `is`,
                  " at",
                  " ",
                  /* @__PURE__ */ jsx6("b", { children: dayjs4(infoTime).format("MMMM D, YYYY [at] h:mm A") }),
                  "."
                ] }) : /* @__PURE__ */ jsxs4(Fragment4, { children: [
                  "This move ",
                  dayjs4(infoTime).isBefore(dayjs4()) ? `was` : `will be`,
                  " ready by",
                  " ",
                  /* @__PURE__ */ jsx6("b", { children: dayjs4(infoTime).format("MMMM D, YYYY [at] h:mm A") }),
                  "."
                ] }) })
              ] }),
              /* @__PURE__ */ jsx6(Spacer_default, {})
            ] });
          })(),
          /* @__PURE__ */ jsxs4("div", { className: cls.flex, children: [
            /* @__PURE__ */ jsx6(
              DatePicker,
              {
                minDate: dayjs4().startOf(`day`),
                maxDate: dayjs4().endOf(`day`).add(1, `month`),
                label: isConciergePickupType(move) ? "Appointment Date" : "Ready By Date",
                value: dayjs4(pickupDate),
                onChange: handleDateChange,
                slotProps: {
                  textField: {
                    error: !validation?.pickupDate,
                    fullWidth: true,
                    size: `small`,
                    variant: `outlined`,
                    helperText: validation?.pickupDate ? `` : `Invalid date.`
                  }
                }
              }
            ),
            /* @__PURE__ */ jsx6(
              TimePicker,
              {
                label: isConciergePickupType(move) ? "Appointment Time" : "Ready By Time",
                value: dayjs4(`${pickupDate} ${pickupTime}`),
                onChange: (time) => {
                  const newTime = time?.format(`HH:mm`) || ``;
                  setPickupTime(newTime);
                  if (newTime && pickupDate) {
                    const selectedDateTime = dayjs4(`${pickupDate} ${newTime}`);
                    const currentDate = dayjs4().format("YYYY-MM-DD");
                    if (pickupDate === currentDate) {
                      const minDateTime = dayjs4().add(1, `minute`);
                      const isValid = !selectedDateTime.isBefore(minDateTime);
                      setValidation((prev) => ({ ...prev, pickupTime: isValid }));
                    } else if (pickupDate < currentDate) {
                      setValidation((prev) => ({ ...prev, pickupTime: false }));
                    } else {
                      setValidation((prev) => ({ ...prev, pickupTime: true }));
                    }
                  }
                },
                slotProps: {
                  textField: {
                    error: !validation?.pickupTime,
                    fullWidth: true,
                    size: `small`,
                    variant: `outlined`,
                    helperText: validation?.pickupTime ? `` : `Invalid time.`
                  }
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsx6(Spacer_default, {}),
          /* @__PURE__ */ jsx6(
            DriverPicker_default,
            {
              drivers,
              ranked,
              loading: driversLoading,
              error: driversError,
              rankingLoading,
              rankingUnavailable,
              value: driverId,
              currentDriverId,
              onChange: setDriverId,
              invalid: !validation?.driverId
            }
          ),
          (formState === "assign" || formState === "unassign") && isConciergeMove(move) && rideshareEnabled && hasRideshareFeature ? /* @__PURE__ */ jsxs4(Fragment4, { children: [
            /* @__PURE__ */ jsx6(Spacer_default, {}),
            /* @__PURE__ */ jsxs4("div", { className: cls.buttonGroup, children: [
              /* @__PURE__ */ jsx6(
                Button3,
                {
                  variant: createRideForReturn ? "contained" : "outlined",
                  color: "primary",
                  disabled: rideshareControlsDisabled,
                  onClick: () => setCreateRideForReturn(true),
                  startIcon: /* @__PURE__ */ jsx6(BiCar2, { size: 24 }),
                  sx: {
                    flex: 1,
                    py: 2,
                    fontSize: "16px",
                    fontWeight: 600,
                    textTransform: "none",
                    opacity: rideshareControlsDisabled ? 0.6 : 1
                  },
                  children: "Dispatch with Rideshare"
                }
              ),
              /* @__PURE__ */ jsx6(
                Button3,
                {
                  variant: !createRideForReturn ? "contained" : "outlined",
                  color: "primary",
                  disabled: rideshareControlsDisabled,
                  onClick: () => setCreateRideForReturn(false),
                  startIcon: /* @__PURE__ */ jsx6(MdDoNotDisturb, { size: 24 }),
                  sx: {
                    flex: 1,
                    py: 2,
                    fontSize: "16px",
                    fontWeight: 600,
                    textTransform: "none",
                    opacity: rideshareControlsDisabled ? 0.6 : 1
                  },
                  children: "Dispatch with no rideshare"
                }
              )
            ] })
          ] }) : null
        ]
      }
    ),
    isSaving ? /* @__PURE__ */ jsx6(LinearProgress2, {}) : null,
    /* @__PURE__ */ jsxs4(ModalFooter, { children: [
      formState === `assign` ? /* @__PURE__ */ jsx6(
        Button3,
        {
          disabled: isSaving,
          variant: "contained",
          color: move?.consumer_type ? `secondary` : `primary`,
          onClick: () => handleSubmit(`assign`),
          startIcon: /* @__PURE__ */ jsx6(BiSolidUserCheck2, { size: 20 }),
          children: "Assign Driver"
        }
      ) : null,
      formState === `unassign` ? /* @__PURE__ */ jsx6(Tooltip2, { placement: "top", title: footerDisabled.reassign.reason, children: /* @__PURE__ */ jsx6("span", { className: cls.footerAction, children: /* @__PURE__ */ jsx6(
        Button3,
        {
          disabled: footerDisabled.reassign.disabled,
          variant: "contained",
          color: move?.consumer_type ? `secondary` : `primary`,
          onClick: () => handleSubmit(`reassign`),
          startIcon: /* @__PURE__ */ jsx6(BiSolidUserCheck2, { size: 20 }),
          children: "Reassign Driver"
        }
      ) }) }) : null,
      formState === `unassign` ? /* @__PURE__ */ jsx6(Tooltip2, { placement: "top", title: footerDisabled.unassign.reason, children: /* @__PURE__ */ jsx6("span", { className: cls.footerAction, children: /* @__PURE__ */ jsx6(
        Button3,
        {
          disabled: footerDisabled.unassign.disabled,
          variant: "contained",
          color: "error",
          onClick: () => handleSubmit(`unassign`),
          startIcon: /* @__PURE__ */ jsx6(BiSolidUserX2, { size: 20 }),
          children: "Unassign Driver"
        }
      ) }) }) : null,
      /* @__PURE__ */ jsx6(Button3, { variant: "text", color: "utility", onClick: () => handleClose(), children: "Cancel" })
    ] })
  ] });
};
var useStyles4 = (theme) => {
  const loader = keyframes`
    0% { opacity: 1; rotate: 0deg; }
    6.25% { opacity: 0.5; }
    12.5% { opacity: 1; }
    18.75% { opacity: 0.5; }
    25% { opacity: 1; }
    31.25% { opacity: 0.5; }
    37.5% { opacity: 1; }
    43.75% { opacity: 0.5; }
    50% { opacity: 1; rotate: 180deg; }
    56.25% { opacity: 0.5; }
    62.5% { opacity: 1; }
    68.75% { opacity: 0.5; }
    75% { opacity: 1; }
    81.25% { opacity: 0.5; }
    87.5% { opacity: 1; }
    93.75% { opacity: 0.5; }
    100% { opacity: 1; rotate: 360deg; }
  `;
  return {
    flex: css4`
      display: flex;
      gap: 12px;
    `,
    // Tooltip anchor for a disabled footer button — inline-flex so wrapping the
    // button changes nothing about how the footer row lays out.
    footerAction: css4`
      display: inline-flex;
    `,
    title: css4`
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;
      background-color: ${theme?.palette?.action?.hover};
    `,
    titleIcon: css4`
      min-width: 20px;
      min-height: 20px;
      margin-top: -2px;
      font-size: 20px;
      color: ${theme?.palette?.text?.secondary};
    `,
    menuItem: css4`
      display: flex;
      align-items: center;
      gap: 12px;
    `,
    iconLoader: css4`
      animation: ${loader} 5s linear infinite;
      user-select: none;
      pointer-events: none;
    `,
    driver: css4`
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      width: fit-content;
      border-radius: 48px;
      padding: 0px 12px 0px 0px;
      background-color: ${theme?.palette?.grey?.[50]};
      cursor: pointer;
    `,
    driverShadow: css4`
      position: absolute;
      top: 0;
      left: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      box-shadow: 0px 0px 6px 2px #00000030 inset;
    `,
    buttonGroup: css4`
      display: flex;
      gap: 12px;
      width: 100%;
    `
  };
};
var AssignOrUnassignMoveModalContent_default = AssignOrUnassignMoveModalContent;
export {
  AssignOrUnassignMoveModalContent_default as AssignOrUnassignMoveModalContent,
  DriverPicker_default as DriverPicker,
  MoveDetailsOffer_default as MoveDetailsOffer,
  RETRACTABLE_RIDE_STATUSES,
  RunDetailOffer_default as RunDetailOffer,
  calculateMovePairTimes,
  canAssignOrUnassignMove,
  convertMoveToMoveGroup,
  dispatchLiteMove,
  driverInitials,
  driverRankGroup,
  ensureDeliverBy,
  findOffersInScenarios,
  formatAuditTime2 as formatAuditTime,
  formatConflictEnd,
  formatDistanceToPickup,
  formatMoveId,
  formatOfferActor2 as formatOfferActor,
  formatOfferTime,
  getDefaultPickupDate,
  getDefaultPickupTime,
  getObjectDiff,
  isConciergeMove,
  isConciergePickupType,
  isOfferAccepted3 as isOfferAccepted,
  isOrderAssignable,
  isRideCallingDisabled,
  isRideRetractable2 as isRideRetractable,
  presentationForAssignmentState2 as presentationForAssignmentState,
  presentationForOfferStatus2 as presentationForOfferStatus,
  refetchMoveWithRelationships,
  resolveAssignBlock,
  resolveDriverAssignment2 as resolveDriverAssignment,
  resolveDriverRow,
  resolveMoveDetailsAssignment,
  resolveMoveDispatchTimes,
  resolveOfferAuditFields2 as resolveOfferAuditFields,
  undispatchLiteMove,
  useDriverRanking
};
//# sourceMappingURL=index.mjs.map